import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, CreditCard, TrendingUp, Clock, Crown, Sparkles, 
  Ban, Gift, Calendar, MoreHorizontal, Search, RefreshCw,
  ChevronDown, Check, X, Loader2, Shield, Tag, Percent, Trash2, ToggleLeft,
  Plus, Edit, Power, Copy, Building2, FolderTree, Puzzle, Bot, FileText, 
  GitBranch, AlertTriangle, Search as SearchIcon, Pencil, FolderKanban, Eye,
  GitPullRequest, UsersRound
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import axios from 'axios';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// AI Usage Panel Component
const AIUsagePanel = () => {
  const [usageData, setUsageData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [days, setDays] = React.useState(30);
  
  const fetchUsageData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/ai-usage/summary?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsageData(response.data);
    } catch (error) {
      console.error('Failed to fetch AI usage:', error);
      toast.error('Fehler beim Laden der KI-Auswertung');
    } finally {
      setLoading(false);
    }
  };
  
  React.useEffect(() => {
    fetchUsageData();
  }, [days]);
  
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
  
  const featureLabels = {
    'research': 'KI-Recherche',
    'template_generation': 'Vorlagen-Generierung',
    'task_suggestion': 'Aufgaben-Vorschläge',
    'ai_report': 'KI-Statusberichte',
    'business_case': 'KI-Business Case',
    'report': 'KI-Berichte',
    'unknown': 'Sonstige'
  };
  
  if (loading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const providerData = usageData?.by_provider ? Object.entries(usageData.by_provider).map(([name, data]) => ({
    name: name === 'openai' ? 'OpenAI' : name === 'perplexity' ? 'Perplexity' : name,
    requests: data.requests,
    cost: data.cost,
    tokens: data.tokens || 0
  })) : [];
  
  const featureData = usageData?.by_feature ? Object.entries(usageData.by_feature).map(([name, data]) => ({
    name: featureLabels[name] || name,
    requests: data.requests,
    cost: data.cost
  })) : [];
  
  const dailyData = usageData?.by_day || [];
  
  return (
    <div className="space-y-6">
      {/* Header with period selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">KI-Nutzung & Kosten</h2>
          <p className="text-sm text-muted-foreground">Übersicht über die Nutzung von KI-Diensten</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days.toString()} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Letzte 7 Tage</SelectItem>
              <SelectItem value="30">Letzte 30 Tage</SelectItem>
              <SelectItem value="90">Letzte 90 Tage</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchUsageData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Gesamtkosten</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              ${usageData?.total_cost?.toFixed(2) || '0.00'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Letzte {days} Tage</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Anfragen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usageData?.total_requests || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">API-Aufrufe gesamt</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">OpenAI Kosten</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              ${usageData?.by_provider?.openai?.cost?.toFixed(2) || '0.00'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {usageData?.by_provider?.openai?.requests || 0} Anfragen
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Perplexity Kosten</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ${usageData?.by_provider?.perplexity?.cost?.toFixed(2) || '0.00'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {usageData?.by_provider?.perplexity?.requests || 0} Anfragen
            </p>
          </CardContent>
        </Card>
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Usage Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tägliche Nutzung</CardTitle>
          </CardHeader>
          <CardContent>
            {dailyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => v.slice(5)}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'cost' ? `$${value.toFixed(4)}` : value,
                      name === 'cost' ? 'Kosten' : 'Anfragen'
                    ]}
                    labelFormatter={(label) => `Datum: ${label}`}
                  />
                  <Line type="monotone" dataKey="requests" stroke="#3b82f6" strokeWidth={2} name="requests" />
                  <Line type="monotone" dataKey="cost" stroke="#10b981" strokeWidth={2} name="cost" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                Keine Daten verfügbar
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Feature Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Nutzung nach Feature</CardTitle>
          </CardHeader>
          <CardContent>
            {featureData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={featureData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="requests"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {featureData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [value, 'Anfragen']} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                Keine Daten verfügbar
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Top Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top Nutzer</CardTitle>
          <CardDescription>Benutzer mit der höchsten KI-Nutzung</CardDescription>
        </CardHeader>
        <CardContent>
          {usageData?.by_user?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Benutzer</TableHead>
                  <TableHead className="text-right">Anfragen</TableHead>
                  <TableHead className="text-right">Kosten</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usageData.by_user.map((user, idx) => (
                  <TableRow key={user.user_id}>
                    <TableCell className="font-medium">{user.name || 'Unbekannt'}</TableCell>
                    <TableCell className="text-right">{user.requests}</TableCell>
                    <TableCell className="text-right">${user.cost.toFixed(4)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              Keine Nutzungsdaten vorhanden
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Pricing Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Preisübersicht</CardTitle>
          <CardDescription>Aktuelle Kosten pro API-Aufruf</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                OpenAI (GPT-4o)
              </h4>
              <p className="text-sm text-muted-foreground">
                Input: $0.0025 / 1K Tokens<br />
                Output: $0.01 / 1K Tokens
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                Perplexity (Sonar)
              </h4>
              <p className="text-sm text-muted-foreground">
                ~$0.005 pro Anfrage<br />
                ($5 pro 1000 Recherchen)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, description, trend }) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      <Icon className="h-4 w-4 text-muted-foreground" />
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      {description && (
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      )}
      {trend && (
        <p className={`text-xs mt-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend > 0 ? '+' : ''}{trend}% vs. Vormonat
        </p>
      )}
    </CardContent>
  </Card>
);

const PlanBadge = ({ plan, status }) => {
  const variants = {
    free: 'bg-gray-500/20 text-gray-600 dark:text-gray-400',
    pro: 'bg-blue-500/20 text-blue-600 dark:text-blue-400',
    business: 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400'
  };
  
  const statusSuffix = status === 'trialing' ? ' (Trial)' : '';
  
  return (
    <Badge className={variants[plan] || variants.free}>
      {plan?.toUpperCase()}{statusSuffix}
    </Badge>
  );
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null });
  const [actionData, setActionData] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [promoCodes, setPromoCodes] = useState([]);
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState(null);
  const [promoDeleteDialog, setPromoDeleteDialog] = useState({ open: false, code: null });
  const [filterOrganization, setFilterOrganization] = useState('all');
  const [featureModules, setFeatureModules] = useState([]);
  const [updatingModule, setUpdatingModule] = useState(null);
  
  // New state for organizations and departments tabs
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const [deptSearchTerm, setDeptSearchTerm] = useState('');
  const [deptFilterOrg, setDeptFilterOrg] = useState('all');
  
  // Organization edit/delete state
  const [editOrgDialog, setEditOrgDialog] = useState({ open: false, org: null });
  const [deleteOrgDialog, setDeleteOrgDialog] = useState({ open: false, org: null });
  const [editOrgData, setEditOrgData] = useState({ name: '', domain: '' });
  
  // Department edit/delete state
  const [editDeptDialog, setEditDeptDialog] = useState({ open: false, dept: null });
  const [deleteDeptDialog, setDeleteDeptDialog] = useState({ open: false, dept: null });
  const [editDeptData, setEditDeptData] = useState({ name: '', description: '' });
  
  // Projects state
  const [allProjects, setAllProjects] = useState([]);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectFilterOrg, setProjectFilterOrg] = useState('all');
  const [projectFilterStatus, setProjectFilterStatus] = useState('all');
  const [editProjectDialog, setEditProjectDialog] = useState({ open: false, project: null });
  const [deleteProjectDialog, setDeleteProjectDialog] = useState({ open: false, project: null });
  const [editProjectData, setEditProjectData] = useState({ name: '', description: '', status: '', planned_cost: '' });
  
  const [newPromoCode, setNewPromoCode] = useState({
    code: '',
    discount_type: 'percent',
    discount_value: 20,
    description: '',
    applies_to_plans: ['pro', 'business'],
    valid_from: '',
    valid_until: '',
    max_uses: '',
    first_payment_only: true
  });
  const navigate = useNavigate();

  // Icon mapping for modules
  const moduleIcons = {
    Search: SearchIcon,
    FileText: FileText,
    GitBranch: GitBranch,
    AlertTriangle: Shield, // Use Shield for Risikomanagement (consistent with rest of app)
    Bot: Bot,
    TrendingUp: TrendingUp,
    GitPullRequest: GitPullRequest,
    Users: UsersRound,
    Shield: Shield,
    Sparkles: Sparkles,
  };

  // Get unique organizations for filter dropdown
  const organizations = useMemo(() => {
    const orgs = new Map();
    users.forEach(u => {
      if (u.organization_id && u.organization_name) {
        orgs.set(u.organization_id, u.organization_name);
      }
    });
    return Array.from(orgs.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const [statsRes, usersRes, promoRes, modulesRes, orgsRes, deptsRes, projectsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/promo-codes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/feature-modules/admin`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/organizations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/departments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/projects`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.status === 403 || usersRes.status === 403) {
        toast.error('Kein Zugriff. Super-Admin Berechtigung erforderlich.');
        navigate('/');
        return;
      }

      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (promoRes.ok) setPromoCodes(await promoRes.json());
      if (modulesRes.ok) setFeatureModules(await modulesRes.json());
      if (orgsRes.ok) setAllOrganizations(await orgsRes.json());
      if (deptsRes.ok) setAllDepartments(await deptsRes.json());
      if (projectsRes.ok) setAllProjects(await projectsRes.json());
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setIsLoading(false);
    }
  };

  const createPromoCode = async () => {
    setIsProcessing(true);
    const token = localStorage.getItem('token');
    
    try {
      // Prepare data - only include valid_from/valid_until if they have values
      const payload = {
        code: newPromoCode.code,
        discount_type: newPromoCode.discount_type,
        discount_value: parseFloat(newPromoCode.discount_value),
        description: newPromoCode.description || null,
        applies_to_plans: newPromoCode.applies_to_plans,
        first_payment_only: newPromoCode.first_payment_only
      };
      
      if (newPromoCode.valid_from) {
        payload.valid_from = new Date(newPromoCode.valid_from).toISOString();
      }
      if (newPromoCode.valid_until) {
        payload.valid_until = new Date(newPromoCode.valid_until).toISOString();
      }
      if (newPromoCode.max_uses && parseInt(newPromoCode.max_uses) > 0) {
        payload.max_uses = parseInt(newPromoCode.max_uses);
      }
      
      const response = await fetch(`${API_URL}/api/promo-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success('Promo-Code erstellt');
        setShowPromoDialog(false);
        resetPromoForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Erstellen');
      }
    } catch (error) {
      toast.error('Ein Fehler ist aufgetreten');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const resetPromoForm = () => {
    setNewPromoCode({
      code: '',
      discount_type: 'percent',
      discount_value: 20,
      description: '',
      applies_to_plans: ['pro', 'business'],
      valid_from: '',
      valid_until: '',
      max_uses: '',
      first_payment_only: true
    });
    setEditingPromoCode(null);
  };
  
  const openEditPromoDialog = (code) => {
    setEditingPromoCode(code);
    setNewPromoCode({
      code: code.code,
      discount_type: code.discount_type,
      discount_value: code.discount_value,
      description: code.description || '',
      applies_to_plans: code.applies_to_plans,
      valid_from: code.valid_from ? code.valid_from.split('T')[0] : '',
      valid_until: code.valid_until ? code.valid_until.split('T')[0] : '',
      max_uses: code.max_uses ? String(code.max_uses) : '',
      first_payment_only: code.first_payment_only
    });
    setShowPromoDialog(true);
  };
  
  const updatePromoCode = async () => {
    if (!editingPromoCode) return;
    setIsProcessing(true);
    const token = localStorage.getItem('token');
    
    try {
      const payload = {
        discount_type: newPromoCode.discount_type,
        discount_value: parseFloat(newPromoCode.discount_value),
        description: newPromoCode.description || null,
        applies_to_plans: newPromoCode.applies_to_plans,
        first_payment_only: newPromoCode.first_payment_only,
        valid_from: newPromoCode.valid_from ? new Date(newPromoCode.valid_from).toISOString() : null,
        valid_until: newPromoCode.valid_until ? new Date(newPromoCode.valid_until).toISOString() : null,
        max_uses: newPromoCode.max_uses ? parseInt(newPromoCode.max_uses) : null
      };
      
      const response = await fetch(`${API_URL}/api/promo-codes/${editingPromoCode.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success('Promo-Code aktualisiert');
        setShowPromoDialog(false);
        resetPromoForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Aktualisieren');
      }
    } catch (error) {
      toast.error('Ein Fehler ist aufgetreten');
    } finally {
      setIsProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Code kopiert!');
  };

  const togglePromoCode = async (codeId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/promo-codes/${codeId}/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const deletePromoCode = async (codeId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/promo-codes/${codeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Code gelöscht');
        fetchData();
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ========== Organization handlers ==========
  const openEditOrgDialog = (org) => {
    setEditOrgData({ name: org.name || '', domain: org.domain || '' });
    setEditOrgDialog({ open: true, org });
  };

  const handleUpdateOrganization = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/admin/organizations/${editOrgDialog.org.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editOrgData)
      });
      
      if (res.ok) {
        toast.success('Organisation aktualisiert');
        setEditOrgDialog({ open: false, org: null });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Aktualisieren');
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteOrganization = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/admin/organizations/${deleteOrgDialog.org.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Organisation gelöscht. ${result.departments_deleted} Abteilungen entfernt, ${result.users_unassigned} Benutzer freigegeben.`);
        setDeleteOrgDialog({ open: false, org: null });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // ========== Department handlers ==========
  const openEditDeptDialog = (dept) => {
    setEditDeptData({ name: dept.name || '', description: dept.description || '' });
    setEditDeptDialog({ open: true, dept });
  };

  const handleUpdateDepartment = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/admin/departments/${editDeptDialog.dept.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editDeptData)
      });
      
      if (res.ok) {
        toast.success('Abteilung aktualisiert');
        setEditDeptDialog({ open: false, dept: null });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Aktualisieren');
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteDepartment = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/admin/departments/${deleteDeptDialog.dept.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Abteilung gelöscht. ${result.users_affected} Benutzer aktualisiert.`);
        setDeleteDeptDialog({ open: false, dept: null });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // ========== Project handlers ==========
  const openEditProjectDialog = (project) => {
    setEditProjectData({ 
      name: project.name || '', 
      description: project.description || '',
      status: project.status || 'active',
      planned_cost: project.planned_cost?.toString() || ''
    });
    setEditProjectDialog({ open: true, project });
  };

  const handleUpdateProject = async () => {
    const token = localStorage.getItem('token');
    try {
      const updateData = {
        name: editProjectData.name,
        description: editProjectData.description,
        status: editProjectData.status
      };
      if (editProjectData.planned_cost) {
        updateData.planned_cost = parseFloat(editProjectData.planned_cost);
      }
      
      const res = await fetch(`${API_URL}/api/admin/projects/${editProjectDialog.project.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (res.ok) {
        toast.success('Projekt aktualisiert');
        setEditProjectDialog({ open: false, project: null });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Aktualisieren');
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteProject = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/admin/projects/${deleteProjectDialog.project.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Projekt gelöscht. ${result.tasks_deleted} Tasks entfernt.`);
        setDeleteProjectDialog({ open: false, project: null });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleAction = async () => {
    if (!selectedUser) return;
    
    setIsProcessing(true);
    const token = localStorage.getItem('token');
    
    try {
      let endpoint = '';
      let method = 'POST';
      let body = null;

      switch (actionDialog.type) {
        case 'changePlan':
          endpoint = `/api/admin/users/${selectedUser.id}/subscription`;
          method = 'PUT';
          body = JSON.stringify({ 
            plan: actionData.plan, 
            reason: actionData.reason 
          });
          break;
        case 'grantFree':
          endpoint = `/api/admin/users/${selectedUser.id}/grant-free`;
          body = JSON.stringify({ 
            plan: actionData.plan || 'pro', 
            duration_days: parseInt(actionData.duration) || 365,
            reason: actionData.reason 
          });
          break;
        case 'extendTrial':
          endpoint = `/api/admin/users/${selectedUser.id}/extend-trial`;
          body = JSON.stringify({ 
            additional_days: parseInt(actionData.days) || 14,
            reason: actionData.reason 
          });
          break;
        case 'cancel':
          endpoint = `/api/admin/users/${selectedUser.id}/cancel-subscription`;
          break;
        case 'block':
          endpoint = `/api/admin/users/${selectedUser.id}/block`;
          break;
        default:
          return;
      }

      const response = await fetch(`${API_URL}${endpoint}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Aktion erfolgreich');
        setActionDialog({ open: false, type: null });
        setActionData({});
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Aktion fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Ein Fehler ist aufgetreten');
    } finally {
      setIsProcessing(false);
    }
  };

  const openActionDialog = (user, type) => {
    setSelectedUser(user);
    setActionData({});
    setActionDialog({ open: true, type });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.organization_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlan = filterPlan === 'all' || user.subscription_plan === filterPlan;
    const matchesOrg = filterOrganization === 'all' || user.organization_id === filterOrganization;
    return matchesSearch && matchesPlan && matchesOrg;
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
              <p className="text-muted-foreground">Plattform-Verwaltung</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Aktualisieren
          </Button>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard 
              title="Benutzer gesamt" 
              value={stats.total_users} 
              icon={Users}
              description={`${stats.active_users} aktiv (30 Tage)`}
            />
            <StatCard 
              title="MRR" 
              value={`CHF ${stats.mrr.toLocaleString('de-CH')}`} 
              icon={TrendingUp}
              description="Monthly Recurring Revenue"
            />
            <StatCard 
              title="Aktive Abos" 
              value={stats.pro_subscriptions + stats.business_subscriptions} 
              icon={CreditCard}
              description={`${stats.pro_subscriptions} Pro, ${stats.business_subscriptions} Business`}
            />
            <StatCard 
              title="Aktive Trials" 
              value={stats.active_trials} 
              icon={Clock}
              description={`${stats.trial_conversion_rate}% Conversion`}
            />
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="bg-gray-500/10 dark:bg-gray-500/20 border-gray-500/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Free User</span>
                <Badge variant="outline">{stats?.free_users || 0}</Badge>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 dark:bg-blue-500/20 border-blue-500/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-600 dark:text-blue-400">Pro User</span>
                <Badge className="bg-blue-600">{stats?.pro_subscriptions || 0}</Badge>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-yellow-500/10 dark:bg-yellow-500/20 border-yellow-500/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-yellow-600 dark:text-yellow-400">Business User</span>
                <Badge className="bg-yellow-600">{stats?.business_subscriptions || 0}</Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content with Tabs */}
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList>
            <TabsTrigger value="users">
              <Users className="h-4 w-4 mr-2" />
              Benutzer
            </TabsTrigger>
            <TabsTrigger value="organizations">
              <Building2 className="h-4 w-4 mr-2" />
              Organisationen
            </TabsTrigger>
            <TabsTrigger value="departments">
              <FolderTree className="h-4 w-4 mr-2" />
              Abteilungen
            </TabsTrigger>
            <TabsTrigger value="projects">
              <FolderKanban className="h-4 w-4 mr-2" />
              Projekte
            </TabsTrigger>
            <TabsTrigger value="promo-codes">
              <Tag className="h-4 w-4 mr-2" />
              Rabattcodes
            </TabsTrigger>
            <TabsTrigger value="modules">
              <Puzzle className="h-4 w-4 mr-2" />
              Module
            </TabsTrigger>
            <TabsTrigger value="ai-usage">
              <Bot className="h-4 w-4 mr-2" />
              KI-Auswertung
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>Benutzer verwalten</CardTitle>
                <CardDescription>
                  Alle registrierten Benutzer und ihre Abonnements
                </CardDescription>
                
                {/* Filters */}
                <div className="flex gap-4 mt-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Suchen nach Name, E-Mail oder Organisation..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select value={filterOrganization} onValueChange={setFilterOrganization}>
                    <SelectTrigger className="w-48">
                      <Building2 className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Organisation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Organisationen</SelectItem>
                      {organizations.map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterPlan} onValueChange={setFilterPlan}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Plan filtern" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Pläne</SelectItem>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="business">Business</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Benutzer</TableHead>
                      <TableHead>Organisation</TableHead>
                      <TableHead>Abteilungen</TableHead>
                      <TableHead>Rolle</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Projekte</TableHead>
                      <TableHead>Registriert</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id} className={user.is_blocked ? 'opacity-50' : ''}>
                        <TableCell>
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {user.name}
                              {user.is_blocked && <Ban className="h-4 w-4 text-red-500" />}
                            </div>
                            <div className="text-sm text-muted-foreground">{user.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.organization_name ? (
                            <div className="flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-sm">{user.organization_name}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {user.department_names && user.department_names.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {user.department_names.map((deptName, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30">
                                  <FolderTree className="h-3 w-3 mr-1" />
                                  {deptName}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.role === 'super_admin' ? 'default' : 'outline'}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <PlanBadge plan={user.subscription_plan} status={user.subscription_status} />
                        </TableCell>
                        <TableCell>{user.project_count}</TableCell>
                        <TableCell className="text-sm">
                          {new Date(user.created_at).toLocaleDateString('de-CH')}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Aktionen</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openActionDialog(user, 'changePlan')}>
                                <CreditCard className="h-4 w-4 mr-2" />
                                Plan ändern
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openActionDialog(user, 'grantFree')}>
                                <Gift className="h-4 w-4 mr-2" />
                                Kostenlos freischalten
                              </DropdownMenuItem>
                              {user.subscription_status === 'trialing' && (
                                <DropdownMenuItem onClick={() => openActionDialog(user, 'extendTrial')}>
                                  <Calendar className="h-4 w-4 mr-2" />
                                  Trial verlängern
                                </DropdownMenuItem>
                              )}
                              {user.subscription_plan !== 'free' && (
                                <DropdownMenuItem 
                                  onClick={() => openActionDialog(user, 'cancel')}
                                  className="text-red-600"
                                >
                                  <X className="h-4 w-4 mr-2" />
                                  Abo kündigen
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => openActionDialog(user, 'block')}
                                className={user.is_blocked ? 'text-green-600' : 'text-red-600'}
                              >
                                <Ban className="h-4 w-4 mr-2" />
                                {user.is_blocked ? 'Entsperren' : 'Sperren'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {filteredUsers.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    Keine Benutzer gefunden
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Organizations Tab */}
          <TabsContent value="organizations">
            <Card>
              <CardHeader>
                <CardTitle>Organisationen verwalten</CardTitle>
                <CardDescription>
                  Alle Organisationen auf der Plattform ({allOrganizations.length} gesamt)
                </CardDescription>
                
                {/* Search */}
                <div className="mt-4">
                  <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Suchen nach Name oder Domain..."
                      value={orgSearchTerm}
                      onChange={(e) => setOrgSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Organisation</TableHead>
                      <TableHead>Inhaber</TableHead>
                      <TableHead className="text-center">Mitglieder</TableHead>
                      <TableHead className="text-center">Abteilungen</TableHead>
                      <TableHead className="text-center">Projekte</TableHead>
                      <TableHead>Erstellt</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allOrganizations
                      .filter(org => 
                        !orgSearchTerm || 
                        org.name?.toLowerCase().includes(orgSearchTerm.toLowerCase()) ||
                        org.domain?.toLowerCase().includes(orgSearchTerm.toLowerCase())
                      )
                      .map((org) => (
                        <TableRow key={org.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                {org.logo_url ? (
                                  <img src={org.logo_url} alt={org.name} className="w-8 h-8 rounded object-cover" />
                                ) : (
                                  <Building2 className="h-5 w-5 text-primary" />
                                )}
                              </div>
                              <div>
                                <div className="font-medium text-foreground">{org.name}</div>
                                {org.domain && (
                                  <div className="text-xs text-muted-foreground">@{org.domain}</div>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="text-sm text-foreground">{org.owner_name || '-'}</div>
                              <div className="text-xs text-muted-foreground">{org.owner_email}</div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">{org.member_count}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{org.department_count}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{org.project_count}</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {org.created_at ? new Date(org.created_at).toLocaleDateString('de-CH') : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openEditOrgDialog(org)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                onClick={() => setDeleteOrgDialog({ open: true, org })}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                
                {allOrganizations.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    Keine Organisationen gefunden
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Departments Tab */}
          <TabsContent value="departments">
            <Card>
              <CardHeader>
                <CardTitle>Abteilungen verwalten</CardTitle>
                <CardDescription>
                  Alle Abteilungen über alle Organisationen ({allDepartments.length} gesamt)
                </CardDescription>
                
                {/* Filters */}
                <div className="flex gap-4 mt-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Suchen nach Abteilungsname..."
                        value={deptSearchTerm}
                        onChange={(e) => setDeptSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select value={deptFilterOrg} onValueChange={setDeptFilterOrg}>
                    <SelectTrigger className="w-[200px]">
                      <Building2 className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Organisation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Organisationen</SelectItem>
                      {allOrganizations.map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Abteilung</TableHead>
                      <TableHead>Organisation</TableHead>
                      <TableHead>Beschreibung</TableHead>
                      <TableHead className="text-center">Mitglieder</TableHead>
                      <TableHead>Erstellt</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allDepartments
                      .filter(dept => {
                        const matchesSearch = !deptSearchTerm || 
                          dept.name?.toLowerCase().includes(deptSearchTerm.toLowerCase());
                        const matchesOrg = deptFilterOrg === 'all' || 
                          dept.organization_id === deptFilterOrg;
                        return matchesSearch && matchesOrg;
                      })
                      .map((dept) => (
                        <TableRow key={dept.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                                <FolderTree className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                              </div>
                              <div className="font-medium text-foreground">{dept.name}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              <Building2 className="h-3 w-3 mr-1" />
                              {dept.organization_name || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
                            {dept.description || '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">{dept.member_count}</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {dept.created_at ? new Date(dept.created_at).toLocaleDateString('de-CH') : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openEditDeptDialog(dept)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                onClick={() => setDeleteDeptDialog({ open: true, dept })}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                
                {allDepartments.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    Keine Abteilungen gefunden
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <Card>
              <CardHeader>
                <CardTitle>Projekte verwalten</CardTitle>
                <CardDescription>
                  Alle Projekte über alle Organisationen ({allProjects.length} gesamt)
                </CardDescription>
                
                {/* Filters */}
                <div className="flex gap-4 mt-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Suchen nach Projektname..."
                        value={projectSearchTerm}
                        onChange={(e) => setProjectSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select value={projectFilterOrg} onValueChange={setProjectFilterOrg}>
                    <SelectTrigger className="w-[200px]">
                      <Building2 className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Organisation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Organisationen</SelectItem>
                      <SelectItem value="none">Ohne Organisation</SelectItem>
                      {allOrganizations.map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={projectFilterStatus} onValueChange={setProjectFilterStatus}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Status</SelectItem>
                      <SelectItem value="active">Aktiv</SelectItem>
                      <SelectItem value="completed">Abgeschlossen</SelectItem>
                      <SelectItem value="on_hold">Pausiert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Projekt</TableHead>
                      <TableHead>Organisation</TableHead>
                      <TableHead>Inhaber</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Geplant</TableHead>
                      <TableHead className="text-right">Effektiv</TableHead>
                      <TableHead className="text-center">Tasks</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allProjects
                      .filter(proj => {
                        const matchesSearch = !projectSearchTerm || 
                          proj.name?.toLowerCase().includes(projectSearchTerm.toLowerCase());
                        const matchesOrg = projectFilterOrg === 'all' || 
                          (projectFilterOrg === 'none' ? !proj.organization_id : proj.organization_id === projectFilterOrg);
                        const matchesStatus = projectFilterStatus === 'all' || 
                          proj.status === projectFilterStatus;
                        return matchesSearch && matchesOrg && matchesStatus;
                      })
                      .map((proj) => {
                        const progress = proj.task_count > 0 ? Math.round((proj.completed_tasks / proj.task_count) * 100) : 0;
                        const costDiff = (proj.effective_cost || 0) - (proj.planned_cost || 0);
                        const isOverBudget = costDiff > 0;
                        
                        return (
                          <TableRow key={proj.id}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                  <FolderKanban className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                  <div className="font-medium text-foreground">{proj.name}</div>
                                  {proj.description && (
                                    <div className="text-xs text-muted-foreground max-w-[200px] truncate">{proj.description}</div>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                <Building2 className="h-3 w-3 mr-1" />
                                {proj.organization_name || 'Keine'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div>
                                <div className="text-sm text-foreground">{proj.owner_name || '-'}</div>
                                <div className="text-xs text-muted-foreground">{proj.owner_email}</div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={
                                proj.status === 'completed' ? 'bg-green-600 text-white' :
                                proj.status === 'on_hold' ? 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400' :
                                'bg-blue-500/20 text-blue-600 dark:text-blue-400'
                              }>
                                {proj.status === 'completed' ? 'Abgeschlossen' : 
                                 proj.status === 'on_hold' ? 'Pausiert' : 'Aktiv'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              CHF {(proj.planned_cost || 0).toLocaleString('de-CH')}
                            </TableCell>
                            <TableCell className={`text-right font-mono text-sm ${isOverBudget ? 'text-red-500' : ''}`}>
                              CHF {(proj.effective_cost || 0).toLocaleString('de-CH')}
                              {isOverBudget && (
                                <span className="block text-xs text-red-500">
                                  +{costDiff.toLocaleString('de-CH')}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <Badge variant="secondary">{proj.completed_tasks}/{proj.task_count}</Badge>
                                <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-green-500 rounded-full" 
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => navigate(`/projects/${proj.id}`)}
                                  title="Projekt öffnen"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => openEditProjectDialog(proj)}
                                  title="Bearbeiten"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                  onClick={() => setDeleteProjectDialog({ open: true, project: proj })}
                                  title="Löschen"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
                
                {allProjects.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    Keine Projekte gefunden
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promo-codes">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Rabattcodes verwalten</CardTitle>
                    <CardDescription>
                      Erstellen und verwalten Sie Promo-Codes für Marketing-Aktionen
                    </CardDescription>
                  </div>
                  <Button onClick={() => { resetPromoForm(); setShowPromoDialog(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Neuer Code
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Rabatt</TableHead>
                      <TableHead>Gültig für</TableHead>
                      <TableHead>Gültigkeit</TableHead>
                      <TableHead>Nutzung</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {promoCodes.map((code) => {
                      const isExpired = code.valid_until && new Date(code.valid_until) < new Date();
                      const isNotYetValid = code.valid_from && new Date(code.valid_from) > new Date();
                      const usageLimitReached = code.max_uses && code.times_used >= code.max_uses;
                      
                      return (
                        <TableRow key={code.id} className={(!code.is_active || isExpired || usageLimitReached) ? 'opacity-60' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <code className="bg-muted px-2 py-1 rounded font-mono text-sm font-semibold">
                                {code.code}
                              </code>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6"
                                onClick={() => copyToClipboard(code.code)}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                            {code.description && (
                              <p className="text-xs text-muted-foreground mt-1">{code.description}</p>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">
                              {code.discount_type === 'percent' 
                                ? `${code.discount_value}%` 
                                : `CHF ${code.discount_value.toFixed(2)}`
                              }
                            </span>
                            {code.first_payment_only && (
                              <p className="text-xs text-muted-foreground">Nur erste Zahlung</p>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {code.applies_to_plans.map((plan) => (
                                <Badge 
                                  key={plan} 
                                  variant="outline"
                                  className={plan === 'pro' ? 'border-blue-500/50 text-blue-600 dark:text-blue-400 bg-blue-500/10' : 'border-yellow-500/50 text-yellow-600 dark:text-yellow-400 bg-yellow-500/10'}
                                >
                                  {plan.toUpperCase()}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            {code.valid_from || code.valid_until ? (
                              <div className="text-sm">
                                {code.valid_from && (
                                  <div className={isNotYetValid ? 'text-orange-600' : ''}>
                                    Ab: {new Date(code.valid_from).toLocaleDateString('de-CH')}
                                  </div>
                                )}
                                {code.valid_until && (
                                  <div className={isExpired ? 'text-red-600' : ''}>
                                    Bis: {new Date(code.valid_until).toLocaleDateString('de-CH')}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">Unbegrenzt</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <span className={usageLimitReached ? 'text-red-600 font-medium' : ''}>
                                {code.times_used}
                              </span>
                              {code.max_uses && (
                                <span className="text-muted-foreground"> / {code.max_uses}</span>
                              )}
                              {!code.max_uses && (
                                <span className="text-muted-foreground"> / ∞</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {isExpired ? (
                              <Badge variant="outline" className="bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/30">
                                Abgelaufen
                              </Badge>
                            ) : usageLimitReached ? (
                              <Badge variant="outline" className="bg-orange-500/20 text-orange-600 dark:text-orange-400 border-orange-500/30">
                                Limit erreicht
                              </Badge>
                            ) : isNotYetValid ? (
                              <Badge variant="outline" className="bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-yellow-500/30">
                                Noch nicht gültig
                              </Badge>
                            ) : code.is_active ? (
                              <Badge className="bg-green-600 text-white hover:bg-green-600">
                                Aktiv
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-gray-500/20 text-gray-600 dark:text-gray-400 border-gray-500/30">
                                Inaktiv
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => openEditPromoDialog(code)}
                                title="Bearbeiten"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => togglePromoCode(code.id)}
                                title={code.is_active ? 'Deaktivieren' : 'Aktivieren'}
                              >
                                <Power className={`h-4 w-4 ${code.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => setPromoDeleteDialog({ open: true, code })}
                                className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                title="Löschen"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                
                {promoCodes.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Tag className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">Keine Rabattcodes vorhanden</p>
                    <p className="text-sm mt-1">Erstellen Sie Ihren ersten Promo-Code für Marketing-Aktionen.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Modules Tab */}
          <TabsContent value="modules">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Puzzle className="h-5 w-5" />
                  Feature-Module verwalten
                </CardTitle>
                <CardDescription>
                  Steuern Sie die Sichtbarkeit und Verfügbarkeit von Modulen für alle Benutzer.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {featureModules.map((module) => {
                    const IconComponent = moduleIcons[module.icon] || Puzzle;
                    return (
                      <div 
                        key={module.id}
                        className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                          module.is_enabled 
                            ? 'bg-green-500/10 border-green-500/30 dark:bg-green-500/20 dark:border-green-500/40' 
                            : module.show_teaser 
                              ? 'bg-amber-500/10 border-amber-500/30 dark:bg-amber-500/20 dark:border-amber-500/40'
                              : 'bg-muted/30 border-border'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-lg ${
                            module.is_enabled 
                              ? 'bg-green-500/20 dark:bg-green-500/30' 
                              : module.show_teaser
                                ? 'bg-amber-500/20 dark:bg-amber-500/30'
                                : 'bg-muted'
                          }`}>
                            <IconComponent className={`h-6 w-6 ${
                              module.is_enabled 
                                ? 'text-green-600 dark:text-green-400' 
                                : module.show_teaser
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-muted-foreground'
                            }`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-foreground">{module.name}</h4>
                              {module.is_enabled ? (
                                <Badge className="bg-green-600 text-white">Aktiv</Badge>
                              ) : module.show_teaser ? (
                                <Badge variant="outline" className="border-amber-500 text-amber-600 dark:text-amber-400">Demnächst</Badge>
                              ) : (
                                <Badge variant="secondary">Versteckt</Badge>
                              )}
                              <Badge variant="outline" className="text-xs">
                                {module.requires_plan === 'business' ? 'Business' : module.requires_plan === 'pro' ? 'Pro' : 'Free'}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{module.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          {/* Position Selection */}
                          <div className="text-center">
                            <Label className="text-xs text-muted-foreground mb-1 block">Position</Label>
                            <Select
                              value={module.nav_position || 'main'}
                              onValueChange={async (value) => {
                                setUpdatingModule(module.id);
                                const token = localStorage.getItem('token');
                                try {
                                  await fetch(`${API_URL}/api/feature-modules/${module.id}`, {
                                    method: 'PUT',
                                    headers: { 
                                      'Authorization': `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({ nav_position: value })
                                  });
                                  fetchData();
                                  toast.success('Position aktualisiert');
                                } catch (e) {
                                  toast.error('Fehler beim Aktualisieren');
                                }
                                setUpdatingModule(null);
                              }}
                              disabled={updatingModule === module.id}
                            >
                              <SelectTrigger className="w-[160px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="main">Hauptnavigation</SelectItem>
                                <SelectItem value="dropdown_projekte">Dropdown Projekte</SelectItem>
                                <SelectItem value="dropdown_settings">Dropdown Einstellungen</SelectItem>
                                <SelectItem value="none">Versteckt (nur URL)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Plan Selection */}
                          <div className="text-center">
                            <Label className="text-xs text-muted-foreground mb-1 block">Plan</Label>
                            <Select
                              value={module.requires_plan}
                              onValueChange={async (value) => {
                                setUpdatingModule(module.id);
                                const token = localStorage.getItem('token');
                                try {
                                  await fetch(`${API_URL}/api/feature-modules/${module.id}`, {
                                    method: 'PUT',
                                    headers: { 
                                      'Authorization': `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({ requires_plan: value })
                                  });
                                  fetchData();
                                  toast.success('Plan aktualisiert');
                                } catch (e) {
                                  toast.error('Fehler beim Aktualisieren');
                                }
                                setUpdatingModule(null);
                              }}
                              disabled={updatingModule === module.id}
                            >
                              <SelectTrigger className="w-[100px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="free">Free</SelectItem>
                                <SelectItem value="pro">Pro</SelectItem>
                                <SelectItem value="business">Business</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Teaser Toggle */}
                          <div className="text-center">
                            <Label className="text-xs text-muted-foreground mb-1 block">Anteasern</Label>
                            <Switch
                              checked={module.show_teaser}
                              onCheckedChange={async (checked) => {
                                setUpdatingModule(module.id);
                                const token = localStorage.getItem('token');
                                try {
                                  await fetch(`${API_URL}/api/feature-modules/${module.id}`, {
                                    method: 'PUT',
                                    headers: { 
                                      'Authorization': `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({ show_teaser: checked })
                                  });
                                  fetchData();
                                  toast.success(checked ? 'Teaser aktiviert' : 'Teaser deaktiviert');
                                } catch (e) {
                                  toast.error('Fehler beim Aktualisieren');
                                }
                                setUpdatingModule(null);
                              }}
                              disabled={updatingModule === module.id || module.is_enabled}
                              className="data-[state=checked]:bg-amber-500"
                            />
                          </div>
                          
                          {/* Active Toggle */}
                          <div className="text-center">
                            <Label className="text-xs text-muted-foreground mb-1 block">Aktiviert</Label>
                            <Switch
                              checked={module.is_enabled}
                              onCheckedChange={async (checked) => {
                                setUpdatingModule(module.id);
                                const token = localStorage.getItem('token');
                                try {
                                  await fetch(`${API_URL}/api/feature-modules/${module.id}`, {
                                    method: 'PUT',
                                    headers: { 
                                      'Authorization': `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({ is_enabled: checked })
                                  });
                                  fetchData();
                                  toast.success(checked ? 'Modul aktiviert' : 'Modul deaktiviert');
                                } catch (e) {
                                  toast.error('Fehler beim Aktualisieren');
                                }
                                setUpdatingModule(null);
                              }}
                              disabled={updatingModule === module.id}
                              className="data-[state=checked]:bg-green-600"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {featureModules.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      Keine Module gefunden
                    </div>
                  )}
                </div>
                
                {/* Legend */}
                <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-medium mb-2">Legende</h4>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">Versteckt</Badge>
                      <span className="text-muted-foreground">Modul ist für Benutzer nicht sichtbar</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="border-amber-500 text-amber-600">Demnächst</Badge>
                      <span className="text-muted-foreground">Modul wird angeteasert, aber nicht nutzbar</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-600">Aktiv</Badge>
                      <span className="text-muted-foreground">Modul ist vollständig nutzbar</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Usage Tab */}
          <TabsContent value="ai-usage">
            <AIUsagePanel />
          </TabsContent>
        </Tabs>

        {/* Action Dialogs */}
        <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ ...actionDialog, open })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actionDialog.type === 'changePlan' && 'Plan ändern'}
                {actionDialog.type === 'grantFree' && 'Kostenlosen Zugang gewähren'}
                {actionDialog.type === 'extendTrial' && 'Trial verlängern'}
                {actionDialog.type === 'cancel' && 'Abonnement kündigen'}
                {actionDialog.type === 'block' && (selectedUser?.is_blocked ? 'Benutzer entsperren' : 'Benutzer sperren')}
              </DialogTitle>
              <DialogDescription>
                {selectedUser && `Für: ${selectedUser.name} (${selectedUser.email})`}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {actionDialog.type === 'changePlan' && (
                <>
                  <div>
                    <Label>Neuer Plan</Label>
                    <Select value={actionData.plan || ''} onValueChange={(v) => setActionData({...actionData, plan: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Plan wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Grund (optional)</Label>
                    <Textarea 
                      value={actionData.reason || ''} 
                      onChange={(e) => setActionData({...actionData, reason: e.target.value})}
                      placeholder="Interner Vermerk..."
                    />
                  </div>
                </>
              )}

              {actionDialog.type === 'grantFree' && (
                <>
                  <div>
                    <Label>Plan</Label>
                    <Select value={actionData.plan || 'pro'} onValueChange={(v) => setActionData({...actionData, plan: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Dauer (Tage)</Label>
                    <Input 
                      type="number" 
                      value={actionData.duration || 365} 
                      onChange={(e) => setActionData({...actionData, duration: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Grund (optional)</Label>
                    <Textarea 
                      value={actionData.reason || ''} 
                      onChange={(e) => setActionData({...actionData, reason: e.target.value})}
                      placeholder="z.B. Partner, Beta-Tester..."
                    />
                  </div>
                </>
              )}

              {actionDialog.type === 'extendTrial' && (
                <>
                  <div>
                    <Label>Zusätzliche Tage</Label>
                    <Input 
                      type="number" 
                      value={actionData.days || 14} 
                      onChange={(e) => setActionData({...actionData, days: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Grund (optional)</Label>
                    <Textarea 
                      value={actionData.reason || ''} 
                      onChange={(e) => setActionData({...actionData, reason: e.target.value})}
                    />
                  </div>
                </>
              )}

              {actionDialog.type === 'cancel' && (
                <p className="text-muted-foreground">
                  Das Abonnement wird sofort gekündigt und der Benutzer auf den Free-Plan zurückgestuft.
                </p>
              )}

              {actionDialog.type === 'block' && (
                <p className="text-muted-foreground">
                  {selectedUser?.is_blocked 
                    ? 'Der Benutzer wird entsperrt und kann sich wieder anmelden.'
                    : 'Der Benutzer wird gesperrt und kann sich nicht mehr anmelden.'
                  }
                </p>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null })}>
                Abbrechen
              </Button>
              <Button onClick={handleAction} disabled={isProcessing}>
                {isProcessing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Bestätigen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Promo Code Create/Edit Dialog */}
        <Dialog open={showPromoDialog} onOpenChange={(open) => { 
          setShowPromoDialog(open); 
          if (!open) resetPromoForm(); 
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5" />
                {editingPromoCode ? 'Rabattcode bearbeiten' : 'Neuen Rabattcode erstellen'}
              </DialogTitle>
              <DialogDescription>
                {editingPromoCode 
                  ? 'Bearbeiten Sie die Einstellungen des Rabattcodes.'
                  : 'Erstellen Sie einen neuen Promo-Code für Marketing-Aktionen.'
                }
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Code */}
              <div>
                <Label htmlFor="promo-code">Code *</Label>
                <Input
                  id="promo-code"
                  placeholder="z.B. BLACKFRIDAY25"
                  value={newPromoCode.code}
                  onChange={(e) => setNewPromoCode({ ...newPromoCode, code: e.target.value.toUpperCase() })}
                  className="font-mono"
                  disabled={!!editingPromoCode}
                />
                {editingPromoCode && (
                  <p className="text-xs text-muted-foreground mt-1">Code kann nicht geändert werden</p>
                )}
              </div>

              {/* Discount Type & Value */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Rabatttyp</Label>
                  <Select 
                    value={newPromoCode.discount_type} 
                    onValueChange={(v) => setNewPromoCode({ ...newPromoCode, discount_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Prozent (%)</SelectItem>
                      <SelectItem value="fixed">Fester Betrag (CHF)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Rabattwert</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      value={newPromoCode.discount_value}
                      onChange={(e) => setNewPromoCode({ ...newPromoCode, discount_value: e.target.value })}
                      className="pr-12"
                      min="0"
                      max={newPromoCode.discount_type === 'percent' ? '100' : undefined}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                      {newPromoCode.discount_type === 'percent' ? '%' : 'CHF'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="promo-desc">Beschreibung (optional)</Label>
                <Input
                  id="promo-desc"
                  placeholder="z.B. Black Friday Aktion 2025"
                  value={newPromoCode.description}
                  onChange={(e) => setNewPromoCode({ ...newPromoCode, description: e.target.value })}
                />
              </div>

              {/* Applies to Plans */}
              <div>
                <Label>Gültig für Pläne</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newPromoCode.applies_to_plans.includes('pro')}
                      onChange={(e) => {
                        const plans = e.target.checked
                          ? [...newPromoCode.applies_to_plans, 'pro']
                          : newPromoCode.applies_to_plans.filter(p => p !== 'pro');
                        setNewPromoCode({ ...newPromoCode, applies_to_plans: plans });
                      }}
                      className="rounded border-gray-300"
                    />
                    <Badge className="bg-blue-600 text-white hover:bg-blue-600">PRO</Badge>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newPromoCode.applies_to_plans.includes('business')}
                      onChange={(e) => {
                        const plans = e.target.checked
                          ? [...newPromoCode.applies_to_plans, 'business']
                          : newPromoCode.applies_to_plans.filter(p => p !== 'business');
                        setNewPromoCode({ ...newPromoCode, applies_to_plans: plans });
                      }}
                      className="rounded border-gray-300"
                    />
                    <Badge className="bg-yellow-600 text-white hover:bg-yellow-600">BUSINESS</Badge>
                  </label>
                </div>
              </div>

              {/* Validity Period */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="valid-from">Gültig ab (optional)</Label>
                  <Input
                    id="valid-from"
                    type="date"
                    value={newPromoCode.valid_from}
                    onChange={(e) => setNewPromoCode({ ...newPromoCode, valid_from: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="valid-until">Gültig bis (optional)</Label>
                  <Input
                    id="valid-until"
                    type="date"
                    value={newPromoCode.valid_until}
                    onChange={(e) => setNewPromoCode({ ...newPromoCode, valid_until: e.target.value })}
                  />
                </div>
              </div>

              {/* Max Uses */}
              <div>
                <Label htmlFor="max-uses">Maximale Nutzungen (optional)</Label>
                <Input
                  id="max-uses"
                  type="number"
                  placeholder="Unbegrenzt"
                  value={newPromoCode.max_uses}
                  onChange={(e) => setNewPromoCode({ ...newPromoCode, max_uses: e.target.value })}
                  min="1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leer lassen für unbegrenzte Nutzung
                </p>
              </div>

              {/* First Payment Only */}
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label className="font-medium">Nur erste Zahlung</Label>
                  <p className="text-xs text-muted-foreground">
                    Rabatt gilt nur für die erste Zahlung des Abos
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={newPromoCode.first_payment_only}
                  onChange={(e) => setNewPromoCode({ ...newPromoCode, first_payment_only: e.target.checked })}
                  className="h-5 w-5 rounded border-gray-300"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPromoDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={editingPromoCode ? updatePromoCode : createPromoCode} 
                disabled={isProcessing || !newPromoCode.code || newPromoCode.applies_to_plans.length === 0}
              >
                {isProcessing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingPromoCode ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Promo Code Confirmation Dialog */}
        <Dialog open={promoDeleteDialog.open} onOpenChange={(open) => setPromoDeleteDialog({ open, code: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Rabattcode löschen
              </DialogTitle>
              <DialogDescription>
                Möchten Sie den Code <strong className="font-mono">{promoDeleteDialog.code?.code}</strong> wirklich löschen?
                Diese Aktion kann nicht rückgängig gemacht werden.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPromoDeleteDialog({ open: false, code: null })}>
                Abbrechen
              </Button>
              <Button 
                variant="destructive" 
                onClick={async () => {
                  await deletePromoCode(promoDeleteDialog.code?.id);
                  setPromoDeleteDialog({ open: false, code: null });
                }}
              >
                Löschen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Organization Dialog */}
        <Dialog open={editOrgDialog.open} onOpenChange={(open) => !open && setEditOrgDialog({ open: false, org: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Organisation bearbeiten</DialogTitle>
              <DialogDescription>
                Ändern Sie die Details der Organisation "{editOrgDialog.org?.name}"
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editOrgData.name}
                  onChange={(e) => setEditOrgData({ ...editOrgData, name: e.target.value })}
                  placeholder="Organisationsname"
                />
              </div>
              <div className="space-y-2">
                <Label>Domain</Label>
                <Input
                  value={editOrgData.domain}
                  onChange={(e) => setEditOrgData({ ...editOrgData, domain: e.target.value })}
                  placeholder="beispiel.com"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOrgDialog({ open: false, org: null })}>
                Abbrechen
              </Button>
              <Button onClick={handleUpdateOrganization}>
                Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Organization Dialog */}
        <Dialog open={deleteOrgDialog.open} onOpenChange={(open) => !open && setDeleteOrgDialog({ open: false, org: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Organisation löschen</DialogTitle>
              <DialogDescription>
                Sind Sie sicher, dass Sie die Organisation "{deleteOrgDialog.org?.name}" löschen möchten?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-red-600 dark:text-red-400">
                  ⚠️ Warnung: Diese Aktion kann nicht rückgängig gemacht werden!
                </p>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                  <li><strong>{deleteOrgDialog.org?.department_count || 0}</strong> Abteilungen werden gelöscht</li>
                  <li><strong>{deleteOrgDialog.org?.member_count || 0}</strong> Benutzer werden von der Organisation getrennt</li>
                  {deleteOrgDialog.org?.project_count > 0 && (
                    <li className="text-red-600 dark:text-red-400">
                      ❌ <strong>{deleteOrgDialog.org?.project_count}</strong> aktive Projekte müssen zuerst gelöscht werden!
                    </li>
                  )}
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteOrgDialog({ open: false, org: null })}>
                Abbrechen
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteOrganization}
                disabled={deleteOrgDialog.org?.project_count > 0}
              >
                Organisation löschen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Department Dialog */}
        <Dialog open={editDeptDialog.open} onOpenChange={(open) => !open && setEditDeptDialog({ open: false, dept: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Abteilung bearbeiten</DialogTitle>
              <DialogDescription>
                Ändern Sie die Details der Abteilung "{editDeptDialog.dept?.name}"
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editDeptData.name}
                  onChange={(e) => setEditDeptData({ ...editDeptData, name: e.target.value })}
                  placeholder="Abteilungsname"
                />
              </div>
              <div className="space-y-2">
                <Label>Beschreibung</Label>
                <Input
                  value={editDeptData.description}
                  onChange={(e) => setEditDeptData({ ...editDeptData, description: e.target.value })}
                  placeholder="Beschreibung der Abteilung"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDeptDialog({ open: false, dept: null })}>
                Abbrechen
              </Button>
              <Button onClick={handleUpdateDepartment}>
                Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Department Dialog */}
        <Dialog open={deleteDeptDialog.open} onOpenChange={(open) => !open && setDeleteDeptDialog({ open: false, dept: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Abteilung löschen</DialogTitle>
              <DialogDescription>
                Sind Sie sicher, dass Sie die Abteilung "{deleteDeptDialog.dept?.name}" löschen möchten?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-red-600 dark:text-red-400">
                  ⚠️ Diese Aktion kann nicht rückgängig gemacht werden!
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>{deleteDeptDialog.dept?.member_count || 0}</strong> Benutzer werden aus dieser Abteilung entfernt.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDeptDialog({ open: false, dept: null })}>
                Abbrechen
              </Button>
              <Button variant="destructive" onClick={handleDeleteDepartment}>
                Abteilung löschen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Project Dialog */}
        <Dialog open={editProjectDialog.open} onOpenChange={(open) => !open && setEditProjectDialog({ open: false, project: null })}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Projekt bearbeiten</DialogTitle>
              <DialogDescription>
                Ändern Sie die Details des Projekts "{editProjectDialog.project?.name}"
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editProjectData.name}
                  onChange={(e) => setEditProjectData({ ...editProjectData, name: e.target.value })}
                  placeholder="Projektname"
                />
              </div>
              <div className="space-y-2">
                <Label>Beschreibung</Label>
                <Input
                  value={editProjectData.description}
                  onChange={(e) => setEditProjectData({ ...editProjectData, description: e.target.value })}
                  placeholder="Projektbeschreibung"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={editProjectData.status} onValueChange={(v) => setEditProjectData({ ...editProjectData, status: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Status wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Aktiv</SelectItem>
                      <SelectItem value="completed">Abgeschlossen</SelectItem>
                      <SelectItem value="on_hold">Pausiert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Geplantes Budget (CHF)</Label>
                  <Input
                    type="number"
                    value={editProjectData.planned_cost}
                    onChange={(e) => setEditProjectData({ ...editProjectData, planned_cost: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditProjectDialog({ open: false, project: null })}>
                Abbrechen
              </Button>
              <Button onClick={handleUpdateProject}>
                Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Project Dialog */}
        <Dialog open={deleteProjectDialog.open} onOpenChange={(open) => !open && setDeleteProjectDialog({ open: false, project: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Projekt löschen</DialogTitle>
              <DialogDescription>
                Sind Sie sicher, dass Sie das Projekt "{deleteProjectDialog.project?.name}" löschen möchten?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-red-600 dark:text-red-400">
                  ⚠️ Warnung: Diese Aktion kann nicht rückgängig gemacht werden!
                </p>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                  <li>Das Projekt wird <strong>permanent</strong> gelöscht</li>
                  <li><strong>{deleteProjectDialog.project?.task_count || 0}</strong> Tasks werden gelöscht</li>
                  <li>Alle Zeiterfassungen bleiben erhalten</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteProjectDialog({ open: false, project: null })}>
                Abbrechen
              </Button>
              <Button variant="destructive" onClick={handleDeleteProject}>
                Projekt löschen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
